// NewUserForm.js
import { useState, useEffect, useRef } from "react"
import { useAddNewUserMutation } from "./usersApiSlice"
import { useNavigate } from "react-router-dom"
import { ROLES } from "../../config/roles"
import useTitle from '../../hooks/useTitle'

const USER_REGEX = /^[A-Za-z0-9]{3,20}$/
const PWD_REGEX = /^[A-Za-z0-9!@#$%]{4,12}$/

const NewUserForm = () => {
    const [addNewUser, { isLoading, isSuccess, isError, error }] = useAddNewUserMutation()
    const navigate = useNavigate()

    const [username, setUsername] = useState('')
    const [validUsername, setValidUsername] = useState(false)
    const [password, setPassword] = useState('')
    const [validPassword, setValidPassword] = useState(false)
    const [role, setRole] = useState("Sale") // Chỉ 1 vai trò, mặc định là Sale

    useTitle('Nhà đẹp - Chỉnh sửa người dùng')

    // Ref để ngăn autofill
    const usernameRef = useRef()
    const passwordRef = useRef()

    useEffect(() => {
        setValidUsername(USER_REGEX.test(username))
    }, [username])

    useEffect(() => {
        setValidPassword(PWD_REGEX.test(password))
    }, [password])

    useEffect(() => {
        if (isSuccess) {
            setUsername('')
            setPassword('')
            setRole("Sale")
            navigate('/dash/users')
        }
    }, [isSuccess, navigate])

    useEffect(() => {
        if (usernameRef.current) {
            usernameRef.current.setAttribute('autocomplete', 'new-username')
        }
        if (passwordRef.current) {
            passwordRef.current.setAttribute('autocomplete', 'new-password')
        }
    }, [])

    const canSave = validUsername && validPassword && !isLoading

    const onSaveUserClicked = async (e) => {
        e.preventDefault()
        if (canSave) {
            await addNewUser({ username, password, roles: [role] }) // Gửi dưới dạng mảng 1 phần tử
        }
    }

    const roleOptions = Object.values(ROLES).map(r => (
        <option key={r} value={r}>{r}</option>
    ))

    return (
        <div className="container-fluid py-4">
            <div className="row justify-content-center">
                <div className="col-12 col-lg-8 col-xl-6">
                    <div className="card shadow border-0">
                        <div className="card-header bg-success text-white text-center">
                            <h4 className="mb-0">
                                <i className="bi bi-person-plus me-2"></i>Thêm người dùng mới
                            </h4>
                        </div>
                        <div className="card-body p-4">

                            {/* Thông báo lỗi */}
                            {isError && (
                                <div className="alert alert-danger alert-dismissible fade show">
                                    <strong>Lỗi:</strong> {error?.data?.message || "Không thể tạo người dùng"}
                                </div>
                            )}

                            <form onSubmit={onSaveUserClicked}>
                                {/* Username */}
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Tên đăng nhập <span className="text-danger">(3-20 chữ cái hoặc số)</span>
                                    </label>
                                    
                                    <div className="input-group">
                                        <span className="input-group-text"></span>
                                        <input
                                            type="text"
                                            className={`form-control ${username && !validUsername ? 'is-invalid' : ''}`}
                                            value={username}
                                            ref={usernameRef}
                                            onChange={(e) => setUsername(e.target.value)}
                                            autoFocus
                                            autoComplete="off"
                                            placeholder="Nhập tên đăng nhập"
                                        />
                                        {username && !validUsername && (
                                            <div className="invalid-feedback">
                                                Tên đăng nhập phải từ 3-20 ký tự chữ cái
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Password */}
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Mật khẩu <span className="text-danger">(4-12 ký tự, cho phép !@#$%)</span>
                                    </label>
                                    
                                    <div className="input-group">
                                        <span className="input-group-text"></span>
                                        <input
                                            type="password"
                                            className={`form-control ${password && !validPassword ? 'is-invalid' : ''}`}
                                            value={password}
                                            ref={passwordRef}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Nhập mật khẩu"
                                        />
                                        {password && !validPassword && (
                                            <div className="invalid-feedback">
                                                Mật khẩu không hợp lệ
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Vai trò - Dropdown đơn */}
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Vai trò</label>
                                    <select
                                        className="form-select"
                                        value={role}
                                        onChange={(e) => setRole(e.target.value)}
                                    >
                                        {roleOptions}
                                    </select>
                                </div>

                                {/* Nút hành động */}
                                <div className="d-flex justify-content-end gap-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-2"
                                        onClick={() => navigate('/dash/users')}
                                        disabled={isLoading}
                                    >
                                        Hủy bỏ
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-success px-2"
                                        disabled={!canSave || isLoading}
                                    >
                                        {isLoading ? (
                                            <>Đang tạo...</>
                                        ) : (
                                            <>Tạo người dùng</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default NewUserForm